import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { PageFilter } from '../../../state/app';
import { AppState } from '../../../state/configureStore';
import {
  clearSampleDevelopmentPl,
  fetchSampleDevelopmentPlList,
  SampleDevelopmentPlState,
  SampleDevelopmentPlListFilter,
} from '../../../state/sampleDevelopmentPl';
import {
  PageContainer,
  PageTitle,
  TableLoading,
  PageButtonGroup,
  DateTimeDisplay,
  SortLabel,
} from '../../components';
import { useFilter, useTableSorting } from '../../hooks';
import { SampleDevelopmentStatusLabel } from '../../../constants/sampleDevelopmentStatus';
import SampleDevelopmentPlFilter from './SampleDevelopmentPlFilter';

const initialSampleDevelopmentPlListFilter: SampleDevelopmentPlListFilter = {
  'Status.equalsTo': '',
};

const SampleDevelopmentPlList: React.FC<RouteComponentProps> = (props) => {
  const dispatch = useDispatch();
  const { history } = props;
  const [filters, setFilters] = useFilter<SampleDevelopmentPlListFilter>(
    initialSampleDevelopmentPlListFilter,
  );

  const [sort, setSort] = useTableSorting({
    orderBy: 'sampleDevNumber',
    direction: 'desc',
  });

  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 15,
  });

  const {
    items: sampleDevelopmentPlList,
    totalItems,
    loading,
  } = useSelector<AppState, SampleDevelopmentPlState>(
    (state) => state.sampleDevelopmentPl,
  );

  const handleChangeFilter = (newFilter: SampleDevelopmentPlListFilter): void => {
    setFilters(newFilter);
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  useEffect(() => {
    dispatch(fetchSampleDevelopmentPlList(filters, { ...paging, sort }));
    return function cleanUp() {
      dispatch(clearSampleDevelopmentPl());
    };
  }, [dispatch, paging, filters, sort]);

  const addNewSampleDevelopmentPl = (): void => {
    history.push('/sampleDevelopmentPl/form');
  };

  const editSampleDevelopmentPl = (id: number): void => {
    history.push(`/sampleDevelopmentPl/form/${id}`);
  };

  return (
    <PageContainer>
      <PageTitle>Sample Development PL</PageTitle>
      <SampleDevelopmentPlFilter
        filters={filters}
        onChangeFilter={handleChangeFilter}
        initialFilter={initialSampleDevelopmentPlListFilter}
      />
      <PageButtonGroup>
        <Button
          variant="contained"
          color="primary"
          onClick={addNewSampleDevelopmentPl}
          startIcon={<AddIcon />}
        >
          New Sample Development PL
        </Button>
      </PageButtonGroup>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>No.</TableCell>
            <TableCell>
              <SortLabel sort={sort} handleSortChange={setSort} field="sampleDevNumber">
                Sample Development Number
              </SortLabel>
            </TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Program</TableCell>
            <TableCell>Brand</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Block</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Created By</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableLoading isLoading={loading} colSpan={3}>
            {sampleDevelopmentPlList.map((sampleDevelopmentPl, index) => (
              <TableRow key={sampleDevelopmentPl.id}>
                <TableCell>{index + paging.pageSize * paging.pageNumber + 1}</TableCell>
                <TableCell>{sampleDevelopmentPl.sampleDevNumber}</TableCell>
                <TableCell>
                  {SampleDevelopmentStatusLabel[sampleDevelopmentPl.status!]}
                </TableCell>
                <TableCell>{sampleDevelopmentPl.block!.program!.name}</TableCell>
                <TableCell>{sampleDevelopmentPl.block!.brand!.name}</TableCell>
                <TableCell>{sampleDevelopmentPl.block!.item}</TableCell>
                <TableCell>{sampleDevelopmentPl.block!.blockNo}</TableCell>
                <TableCell>
                  <DateTimeDisplay value={sampleDevelopmentPl.createdAt} />
                </TableCell>
                <TableCell>{sampleDevelopmentPl.createdBy}</TableCell>
                <TableCell>
                  <Tooltip title={'Edit'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => editSampleDevelopmentPl(sampleDevelopmentPl.id!)}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableLoading>
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[15, 25, 35]}
        component="div"
        count={totalItems}
        rowsPerPage={paging.pageSize}
        page={paging.pageNumber}
        onPageChange={handleChangePageNumber}
        onRowsPerPageChange={handleChangePageSize}
      />
    </PageContainer>
  );
};

export default SampleDevelopmentPlList;
